import * as React from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from "@authe/ui-core";
import { ROUTES } from "@/config";
import { useLandingAccess } from "./useLandingAccess";

/** Starting work never requires a wallet, an invitation, an ABI or a pipeline. */
export function QuickStart({ destinations }: { destinations: ReadonlySet<string> }): React.ReactElement {
  const transactions = destinations.has(ROUTES.transactions);
  const addresses = destinations.has(ROUTES.addresses);
  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader>
        <h2 className="text-lg font-semibold">{transactions || addresses ? "Start with a transaction or address" : "Your workspace"}</h2>
        <CardDescription>
          {transactions || addresses
            ? "Explore on-chain activity and follow the trail. You can start without connecting a wallet or configuring a pipeline."
            : "The navigation shows the tools available to your role. If a tool you need is missing, ask your workspace administrator for access."}
        </CardDescription>
      </CardHeader>
      {(transactions || addresses) && (
        <CardContent className="flex flex-wrap gap-2">
          {transactions && <Button asChild><Link to={ROUTES.transactions}>Explore transactions<ArrowRight className="h-4 w-4" /></Link></Button>}
          {addresses && <Button asChild variant="outline"><Link to={ROUTES.addresses}>Explore addresses</Link></Button>}
        </CardContent>
      )}
    </Card>
  );
}

const OPTIONS = [
  { to: ROUTES.investigations, title: "Organize an investigation", description: "Keep a trail, evidence and conclusions together in a case.", cta: "Open investigations" },
  { to: ROUTES.catalog, title: "Understand your catalog", description: "Use labels, tags and entities to give addresses meaning. Shared defaults are already available.", cta: "Open catalog" },
  { to: ROUTES.team, title: "Work with your team", description: "View your teammates and collaborate. Administrators can invite others when needed.", cta: "Open team" },
  { to: ROUTES.contracts, title: "Decode your contracts", description: "Add contract ABIs when you need to decode calls and events for your own contracts.", cta: "Open contracts" },
  { to: ROUTES.pipelines, title: "Automate a workflow", description: "Configure a pipeline when you are ready to process transactions automatically.", cta: "Open pipelines" },
  { to: ROUTES.wallet, title: "Connect your wallet", description: "Connect a wallet if you want to view your own activity or sign an action.", cta: "Open wallet" },
];

export interface GettingStartedProps { onDismiss?: () => void }

/** Optional guidance, not inferred completion of unrelated feature inventories. */
export function GettingStarted({ onDismiss }: GettingStartedProps): React.ReactElement {
  const { destinations } = useLandingAccess();
  const options = OPTIONS.filter((option) => destinations.has(option.to));
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-semibold tracking-tight">Get started with Authe</h1>
          <p className="mt-1 text-sm text-muted-foreground">Your workspace is ready. Choose what you want to do; the options below are not required setup steps.</p>
        </div>
        {onDismiss && <Button variant="outline" onClick={onDismiss}>Go to your queue</Button>}
      </div>
      <QuickStart destinations={destinations} />
      {options.length > 0 && <section aria-labelledby="workspace-options">
        <h2 id="workspace-options" className="mb-3 text-lg font-semibold">When you need more</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {options.map((option) => <Card key={option.to} className="flex flex-col">
            <CardHeader><CardTitle className="text-base">{option.title}</CardTitle><CardDescription>{option.description}</CardDescription></CardHeader>
            <CardContent className="mt-auto"><Button asChild variant="secondary" size="sm"><Link to={option.to}>{option.cta}<ArrowRight className="h-4 w-4" /></Link></Button></CardContent>
          </Card>)}
        </div>
      </section>}
    </div>
  );
}
